/**
 * PWA utilities for detection and cache management
 */

import { logger } from '@/lib/logger';

interface CacheClearResult {
  cleared: number;
  failed: number;
  total: number;
}

/**
 * Detects if the app is running in PWA/standalone mode
 * Works for both iOS (navigator.standalone) and Android/Desktop (display-mode: standalone)
 */
export function isPWA(): boolean {
  if (typeof window === 'undefined') return false;

  // iOS standalone mode (Safari "Add to Home Screen")
  // Navigator.standalone is a non-standard Safari property
  if ((window.navigator as Navigator & { standalone?: boolean }).standalone === true) return true;

  // Android/Desktop PWA (display-mode: standalone from manifest)
  if (window.matchMedia('(display-mode: standalone)').matches) return true;

  return false;
}

/**
 * Clears all service worker caches.
 * Should be called on logout to ensure user data isn't cached.
 *
 * Returns statistics about the clearing operation. Uses Promise.allSettled
 * to ensure all caches are attempted even if some fail.
 *
 * Note: This must be called from client-side code (not server actions)
 * since it accesses browser APIs.
 */
export async function clearServiceWorkerCaches(): Promise<CacheClearResult> {
  if (typeof window === 'undefined') return { cleared: 0, failed: 0, total: 0 };
  if (!('caches' in window)) return { cleared: 0, failed: 0, total: 0 };

  const names = await caches.keys();
  const results = await Promise.allSettled(names.map((name) => caches.delete(name)));

  const cleared = results.filter((r) => r.status === 'fulfilled' && r.value).length;
  const failed = results.length - cleared;

  if (failed > 0) {
    logger.warn('Some service worker caches failed to clear', {
      cleared,
      failed,
      total: names.length,
    });
  }

  return { cleared, failed, total: names.length };
}
